import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, MapPin, Briefcase, Calendar, MessageCircle, Loader2, ArrowRight } from 'lucide-react';
import config from '../config';

export const InstallerProfilePage = () => {
  const { id } = useParams();
  const [installer, setInstaller] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInstallerData();
  }, [id]);

  const fetchInstallerData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // دریافت اطلاعات نصاب از API
      const installerResponse = await fetch(config.getApiUrl(`/accounts/installers/${id}/`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (installerResponse.ok) {
        const installerData = await installerResponse.json();
        setInstaller(installerData);
      } else {
        setError('نصاب یافت نشد');
      }
      
      // دریافت نظرات
      const reviewsResponse = await fetch(config.getApiUrl(`/reviews/?installer=${id}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (reviewsResponse.ok) {
        const reviewsData = await reviewsResponse.json();
        setReviews(reviewsData.results || []);
      }
      
    } catch (error) {
      console.error('Error:', error);
      setError('خطا در بارگذاری اطلاعات');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !installer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">نصاب یافت نشد</h2>
          <Link to="/installers" className="mt-4 inline-flex items-center gap-1 text-blue-600">
            بازگشت به لیست نصاب‌ها <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="mb-6">
          <Link to="/installers" className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
            <ArrowRight className="w-4 h-4" /> بازگشت به لیست نصاب‌ها
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center">
                <Briefcase className="w-12 h-12 text-blue-600" />
              </div>
              <div className="text-center md:text-right">
                <h1 className="text-3xl font-bold">{installer.company_name}</h1>
                <div className="flex items-center justify-center md:justify-start gap-4 mt-2">
                  <div className="flex items-center gap-1">
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <span className="font-bold">{installer.rating || 0}</span>
                    <span className="text-blue-100">({installer.total_reviews || 0} نظر)</span>
                  </div>
                  <span className="text-blue-100">|</span>
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{(installer.service_areas || []).join('، ') || 'سراسر کشور'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8">
            {/* Main Info */}
            <div className="lg:col-span-2 space-y-8">
              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-4">درباره ما</h2>
                <p className="text-gray-600 leading-relaxed">{installer.bio || 'توضیحاتی ثبت نشده است'}</p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-4">آمار کاری</h2>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-blue-700">{installer.experience_years || 0}+</div>
                    <div className="text-sm text-gray-600">سال تجربه</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-blue-700">{installer.completed_projects || 0}</div>
                    <div className="text-sm text-gray-600">پروژه تکمیل شده</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-blue-700">{installer.total_reviews || 0}</div>
                    <div className="text-sm text-gray-600">نظر مثبت</div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-4">نظرات مشتریان</h2>
                {reviews.length === 0 ? (
                  <p className="text-gray-500">هنوز نظری ثبت نشده است</p>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div key={review.id} className="border border-gray-100 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                              <span className="font-bold text-gray-600">{review.reviewer?.username?.[0] || 'U'}</span>
                            </div>
                            <span className="font-medium">{review.reviewer?.username || 'کاربر'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            <span>{review.rating}</span>
                          </div>
                        </div>
                        <p className="text-gray-600 text-sm">{review.comment}</p>
                        <p className="text-xs text-gray-400 mt-2">{new Date(review.created_at).toLocaleDateString('fa-IR')}</p>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-bold text-gray-900 mb-4">تماس با ما</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-600">
                    <MapPin className="w-5 h-5" />
                    <span>{(installer.service_areas || ['تهران'])[0]}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-600">
                    <Calendar className="w-5 h-5" />
                    <span>موجود برای پروژه جدید</span>
                  </div>
                </div>
                <button className="w-full mt-4 bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 flex items-center justify-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  درخواست مشاوره
                </button>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-bold text-gray-900 mb-4">اطلاعات مجوز</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">شماره مجوز:</span>
                    <span className="font-medium">{installer.license_number || '---'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">وضعیت:</span>
                    <span className="text-green-600 font-medium">تایید شده</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
